import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, Smile, Trash2, MessageSquare, Film, Tv } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import KarmaBadge from "@/components/KarmaBadge";
import { toast } from "sonner";

const SMILEY_RE = /:([a-z0-9_]{2,24}):/gi;
const MENTION_RE = /\[\[([^\[\]]{2,80})\]\]/g;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString();
}

function renderText(text, smilieMap, mentions, openMention) {
  // First substitute smilies, then mentions
  const parts = [];
  let lastIdx = 0;
  const regex = new RegExp(SMILEY_RE.source + "|" + MENTION_RE.source, "gi");
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    if (m[1]) {
      const sm = smilieMap[m[1].toLowerCase()];
      parts.push(
        sm
          ? <img key={m.index} src={sm.url} alt={`:${m[1]}:`} title={`:${m[1]}:`} className="inline-block w-5 h-5 align-text-bottom mx-0.5" />
          : <span key={m.index} className="text-[var(--tt-text-muted)]">:{m[1]}:</span>
      );
    } else if (m[2]) {
      const title = m[2];
      const mention = mentions?.find((x) => x.raw === title);
      parts.push(
        mention ? (
          <button
            key={m.index}
            onClick={() => openMention(mention)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] font-semibold hover:bg-[var(--tt-primary)]/25"
          >
            {mention.media_type === "tv" ? <Tv className="w-3 h-3" /> : <Film className="w-3 h-3" />}
            {mention.title}{mention.year ? ` (${mention.year})` : ""}
          </button>
        ) : (
          <span key={m.index} className="text-[var(--tt-text-muted)]">[[{title}]]</span>
        )
      );
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

export default function ChatPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [smilies, setSmilies] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const smilieMap = React.useMemo(
    () => Object.fromEntries(smilies.map((s) => [s.shortcode, s])), [smilies]
  );

  const load = async (after) => {
    try {
      const { data } = await api.get("/chat", { params: { after, limit: 80 } });
      if (after) {
        if (data.length) setMessages((m) => [...m, ...data]);
      } else {
        setMessages(data);
      }
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chat/smilies");
        setSmilies(data);
      } catch {}
    })();
    load();
    const t = setInterval(() => {
      const last = messages[messages.length - 1];
      load(last?.created_at);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !user) return;
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { text: text.trim() });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  const removeMsg = async (id) => {
    try {
      await api.delete(`/chat/${id}`);
      setMessages((m) => m.filter((x) => x.message_id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const insertAtCursor = (insertion) => {
    const ta = inputRef.current;
    if (!ta) { setText((t) => t + insertion); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    const newVal = text.slice(0, start) + insertion + text.slice(end);
    setText(newVal);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + insertion.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const openMention = (men) => {
    // Navigate via existing search route — keeps things simple
    if (men?.title) {
      window.location.href = `/?cat=search&q=${encodeURIComponent(men.title)}&type=${men.media_type === "tv" ? "tv" : "movie"}`;
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-hidden flex flex-col">
        <Header />
        <div className="flex-1 overflow-hidden flex flex-col max-w-4xl mx-auto w-full px-4 md:px-8 py-4">
          <header className="mb-3 flex items-center gap-3">
            <h1 className="font-display text-2xl font-extrabold flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-[var(--tt-primary)]" />
              Lounge
            </h1>
            <span className="text-xs text-[var(--tt-text-muted)]">
              Use <code className="bg-[var(--tt-surface-2)] px-1 rounded">:smile:</code> for emoji and <code className="bg-[var(--tt-surface-2)] px-1 rounded">[[Inception]]</code> to link a title.
            </span>
          </header>

          <ul
            ref={listRef}
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-4 space-y-3"
          >
            {messages.length === 0 && (
              <li className="text-center text-[var(--tt-text-muted)] text-sm py-10">
                No messages yet — say hi!
              </li>
            )}
            {messages.map((m) => {
              const isOwn = user && m.user_id === user.user_id;
              const canMod = user && (user.role === "admin" || user.role === "mod" || isOwn);
              return (
                <li key={m.message_id} data-testid={`chat-msg-${m.message_id}`} className="flex gap-3 items-start">
                  {m.user_picture ? (
                    <img src={m.user_picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-xs font-bold shrink-0">
                      {m.user_name[0].toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <Link to={`/u/${m.user_id}`} className="font-semibold text-sm hover:text-[var(--tt-primary)]">{m.user_name}</Link>
                      {m.user_role === "admin" && <span className="text-[9px] uppercase font-bold bg-[var(--tt-yellow)] text-black px-1 rounded">admin</span>}
                      {m.user_role === "mod" && <span className="text-[9px] uppercase font-bold bg-[var(--tt-purple)] text-white px-1 rounded">mod</span>}
                      <span className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt">{timeAgo(m.created_at)}</span>
                      {canMod && (
                        <button onClick={() => removeMsg(m.message_id)} className="ml-auto text-[var(--tt-text-faint)] hover:text-red-400" title="Delete">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="text-sm break-words whitespace-pre-wrap mt-0.5">
                      {renderText(m.text, smilieMap, m.mentions, openMention)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {user ? (
            <form onSubmit={submit} className="mt-3 relative">
              {showPicker && (
                <div data-testid="smiley-picker" className="absolute bottom-full mb-2 left-0 right-0 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-xl p-2 max-h-44 overflow-y-auto grid grid-cols-8 sm:grid-cols-12 gap-1">
                  {smilies.length === 0 && <div className="col-span-full text-xs text-[var(--tt-text-muted)] py-2 text-center">No smilies yet — ask an admin to add some.</div>}
                  {smilies.map((s) => (
                    <button
                      key={s.shortcode}
                      type="button"
                      onClick={() => { insertAtCursor(`:${s.shortcode}:`); setShowPicker(false); }}
                      title={`:${s.shortcode}:`}
                      className="p-1 rounded hover:bg-white/10"
                    >
                      <img src={s.url} alt={s.shortcode} className="w-6 h-6" />
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-end">
                <button type="button" onClick={() => setShowPicker((s) => !s)} data-testid="chat-smiley-btn" className="p-2 rounded-full bg-[var(--tt-surface-2)] hover:bg-[#222]">
                  <Smile className="w-4 h-4" />
                </button>
                <textarea
                  ref={inputRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
                  rows={1}
                  maxLength={500}
                  data-testid="chat-input"
                  placeholder="Say something... :fire: [[Inception]]"
                  className="flex-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-2xl px-4 py-2 text-sm outline-none focus:border-[var(--tt-primary)] resize-none"
                />
                <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send" className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-semibold rounded-full px-4 py-2 flex items-center gap-1 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </form>
          ) : (
            <div className="mt-3 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-xl p-3 text-sm text-[var(--tt-text-muted)] text-center">
              <Link to="/login" className="text-[var(--tt-primary)] font-semibold hover:underline">Login</Link> to join the chat.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
